import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { localizedAlternates, localizedPath, useLang } from "@/lib/i18n";
import { instrumentCoverSrc } from "@/lib/instrumentSeo";
import api from "@/lib/apiClient";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Clock, Compass, Heart, Sparkles, User, Leaf } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const catIcon = { heart: Heart, user: User, sparkles: Sparkles, compass: Compass };

export default function Home() {
  const { t, lang, pick } = useLang();
  const [instruments, setInstruments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [faq, setFaq] = useState([]);

  useSEO({
    title: lang === "id"
      ? "Mindovia — Platform Eksplorasi Diri (Self-Exploration)"
      : "Mindovia — Self-Exploration Platform",
    description: lang === "id"
      ? "Platform edukatif untuk refleksi diri melalui instrumen berbasis riset. Kenali diri lewat kuesioner singkat — anonim, aman, bilingual."
      : "Educational self-reflection platform with research-based instruments. Discover yourself through short questionnaires — anonymous, safe, bilingual.",
    url: localizedPath("/", lang),
    alternates: localizedAlternates("/"),
    image: "/og-cover.png",
  });

  // Hide themes that have no published test yet (until instruments load, show all).
  const usedCategories = new Set(instruments.flatMap((i) => [...(i.categories || []).map((c) => c.slug), i.category?.slug]).filter(Boolean));
  const visibleCategories = instruments.length ? categories.filter((c) => usedCategories.has(c.slug)) : categories;

  useEffect(() => {
    api.get("/public/instruments").then(r => setInstruments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/public/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/public/faq").then(r => setFaq(r.data?.items || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-cream-50 via-background to-terracotta-50/60" aria-hidden />
        <div className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-terracotta-200/30 blur-3xl -z-10" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 space-y-7 animate-fade-up">
            <Badge variant="outline" className="text-xs tracking-wider font-medium border-terracotta-200 bg-cream-50 text-teal-800 uppercase" data-testid="hero-badge">
              <Leaf className="w-3 h-3 mr-1.5" /> {t("hero.badge")}
            </Badge>
            <h1 className="font-display font-medium text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-2xl">
              {t("hero.title")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link to={localizedPath("/instruments", lang)}>
                <Button data-testid="hero-cta" size="lg" className="rounded-full px-7 h-12 bg-terracotta-500 hover:bg-terracotta-600 text-cream-50 shadow-sm">
                  {t("hero.cta")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button data-testid="hero-secondary" size="lg" variant="outline" className="rounded-full px-6 h-12 border-teal-800/40 text-teal-800 hover:bg-cream-100">
                  {t("hero.secondary")}
                </Button>
              </a>
            </div>
            <ul className="flex flex-wrap gap-2 text-xs text-teal-800" data-testid="hero-chips">
              {["hero.chip.free", "hero.chip.time", "hero.chip.account"].map((k) => (
                <li key={k} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border">
                  <Check className="w-3.5 h-3.5 text-terracotta-500" /> {t(k)}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <CoverStack instruments={instruments} lang={lang} pick={pick} />
          </div>
        </div>
      </section>

      {/* Featured instruments */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("home.featured")}</h2>
          <Link to="/instruments" className="text-sm text-primary hover:underline" data-testid="home-see-all">
            {lang === "id" ? "Lihat semua →" : "See all →"}
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {instruments.slice(0, 4).map((inst) => (
            <Link
              key={inst.id}
              to={localizedPath(`/instruments/${inst.slug}`, lang)}
              data-testid={`featured-instrument-${inst.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="aspect-[4/5] overflow-hidden bg-secondary">
                <img src={instrumentCoverSrc(inst, lang)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4 md:p-5 flex flex-col flex-1">
                <h3 className="font-display text-base md:text-lg leading-snug">{pick(inst, "name")}</h3>
                <p className="hidden md:block text-sm text-muted-foreground line-clamp-2 mt-1.5">{pick(inst, "short_description")}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto pt-3">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {inst.duration_minutes} {t("cat.min")}</span>
                  <span>{inst.question_count} {t("cat.questions")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">{t("home.categories")}</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {visibleCategories.map((c) => {
            const Icon = catIcon[c.icon] || Compass;
            return (
              <Link
                key={c.id}
                to={localizedPath(`/instruments?category=${c.slug}`, lang)}
                data-testid={`category-${c.slug}`}
                className="p-6 rounded-2xl border border-border bg-card hover:border-terracotta-300 hover:bg-cream-50 transition-colors group"
              >
                <div className="w-11 h-11 rounded-full bg-terracotta-500/10 text-terracotta-600 grid place-items-center mb-4 group-hover:bg-terracotta-500 group-hover:text-cream-50 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg tracking-tight">{pick(c, "name")}</div>
                <div className="text-sm text-muted-foreground mt-1">{pick(c, "description")}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-12">{t("home.how")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="relative p-8 rounded-2xl bg-card border border-border">
              <div className="text-6xl font-display text-terracotta-500/25 absolute top-4 right-6 select-none">0{n}</div>
              <div className="font-display text-xl mb-2">{t(`home.how.${n}.title`)}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{t(`home.how.${n}.desc`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="p-6 rounded-xl border border-terracotta-200 bg-terracotta-50/50 text-sm text-teal-800">
          <strong className="font-display">Disclaimer.</strong> {t("home.disclaimer")}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">{t("home.faq")}</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
          {(faq || []).map((it, k) => (
            <AccordionItem value={String(k)} key={it.key || k} data-testid={`faq-item-${it.key || k}`}>
              <AccordionTrigger className="text-left font-display text-lg">
                {lang === "id" ? (it.question_id || "") : (it.question_en || it.question_id || "")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line break-words">
                {lang === "id" ? (it.answer_id || "") : (it.answer_en || it.answer_id || "")}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}

// Hero visual: a fan of real test covers, so visitors see what the site offers at a glance.
function CoverStack({ instruments, lang, pick }) {
  const covers = instruments.filter((i) => instrumentCoverSrc(i, lang)).slice(0, 3);
  if (covers.length === 0) {
    return <div className="mx-auto w-full max-w-sm aspect-[4/5] rounded-3xl bg-secondary/60 animate-pulse" aria-hidden />;
  }
  const layout = [
    "z-10 -rotate-6 -translate-x-[38%] translate-y-6 scale-90",
    "z-20 rotate-6 translate-x-[38%] translate-y-6 scale-90",
    "z-30 rotate-0",
  ];
  const ordered = covers.length === 3 ? covers : covers.slice().reverse();
  return (
    <div className="relative mx-auto w-full max-w-md h-[22rem] sm:h-[26rem] md:h-[30rem]" data-testid="hero-cover-stack">
      {ordered.map((inst, k) => (
        <Link
          key={inst.id}
          to={localizedPath(`/instruments/${inst.slug}`, lang)}
          className={`absolute left-1/2 top-0 -ml-[30%] w-[60%] aspect-[4/5] rounded-2xl overflow-hidden border-4 border-card shadow-2xl transition-transform duration-500 hover:-translate-y-2 ${layout[3 - ordered.length + k]}`}
          aria-label={pick(inst, "name")}
        >
          <img src={instrumentCoverSrc(inst, lang)} alt="" className="w-full h-full object-cover" />
        </Link>
      ))}
    </div>
  );
}
